import React, { useCallback, useEffect, useRef } from 'react';
import SavedMessageItem from './SavedMessageItem';

const SavedMessageList = ({
  filteredMessages,
  channels,
  directMessages,
  getChannelName,
  onNavigate,
  selectedChannelId,
  hasAnySavedMessages = true,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}) => {
  const showChannelName = selectedChannelId === null;
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);

  const handleScroll = useCallback(() => {
    if (!hasMore || loadingMore || !onLoadMore) return;
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 240) {
      onLoadMore();
    }
  }, [hasMore, loadingMore, onLoadMore]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (!hasMore || loadingMore || !onLoadMore) return undefined;
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onLoadMore();
        }
      },
      { root: scrollRef.current, rootMargin: '200px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, onLoadMore, filteredMessages.length]);

  if (filteredMessages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4">
        <svg
          className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
          />
        </svg>
        <p className="text-gray-500 dark:text-gray-400 text-lg">
          {hasAnySavedMessages ? 'No bookmarks found' : 'No bookmarks yet'}
        </p>
        <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
          {hasAnySavedMessages
            ? 'Try a different channel or search term.'
            : 'Bookmark a message or shared resource to see it here'}
        </p>
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
      <div className="space-y-3">
        {filteredMessages.map((savedMessage) => (
          <SavedMessageItem
            key={savedMessage.id}
            savedMessage={savedMessage}
            channels={channels}
            directMessages={directMessages}
            onNavigate={onNavigate}
            showChannelName={showChannelName}
            channelName={getChannelName(savedMessage.channelId)}
          />
        ))}
        {hasMore && <div ref={sentinelRef} className="h-1" />}
        {loadingMore && (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400" />
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedMessageList;
